import { useTranslate } from '@festina-life/ui/hooks'
import { SimpleModal } from '@festina-life/ui/structure'
import { useState } from 'react'

import { templateIdFromPath } from '../../uiModels/templateOperations/helpers'
import type { FieldOrigin } from '../../uiModels/types'
import { SubTemplateCreationCheckbox } from './SubTemplateCreationCheckbox'
import { useCollectInexistentSubtemplatePaths } from './useCollectInexistentSubtemplatePaths'

type Props = {
  onCopyValue: () => void
  onCancel: () => void
  path: string
  fromOrigin: FieldOrigin
}

export const CopyInheritedValueModal = ({
  onCopyValue,
  onCancel,
  path,
  fromOrigin,
}: Props) => {
  const translate = useTranslate()
  const subtemplateCreationRequirements =
    useCollectInexistentSubtemplatePaths(path)
  const identifiableValueSetups = subtemplateCreationRequirements.filter(
    (requirement) => requirement.isIdentifiable,
  )
  const [
    didUserAcceptSubtemplateCreation,
    setDidUserAcceptSubTemplateCreation,
  ] = useState(!subtemplateCreationRequirements.length)
  const [
    didUserAcceptIdentifiableCreation,
    setDidUserAcceptIdentifiableCreation,
  ] = useState(!identifiableValueSetups.length)
  const isCopyDisabled =
    !didUserAcceptIdentifiableCreation || !didUserAcceptSubtemplateCreation

  return (
    <SimpleModal
      isOpen
      closeConfiguration={{ onClose: onCancel, label: translate('cancel') }}
      acceptConfiguration={{
        onAccept: onCopyValue,
        disabled: isCopyDisabled,
        label: translate('templateViewer.copyInheritedValueModal.copyButton'),
      }}
      title={translate('templateViewer.copyInheritedValueModal.title')}
      bodyOuterClassName="max-w-4xl"
    >
      <div className="flex flex-col gap-y-2">
        <p>
          {translate('templateViewer.copyInheritedValueModal.description', {
            templateIdentifier: templateIdFromPath(fromOrigin),
          })}
        </p>
        {!!subtemplateCreationRequirements.length && (
          <SubTemplateCreationCheckbox
            id="copySubtemplates"
            label={translate(
              'templateViewer.copyInheritedValueModal.subtemplatesCheckbox',
            )}
            isChecked={didUserAcceptSubtemplateCreation}
            onToggle={() =>
              setDidUserAcceptSubTemplateCreation(
                !didUserAcceptSubtemplateCreation,
              )
            }
            paths={subtemplateCreationRequirements.map(
              (requirement) => requirement.path,
            )}
          />
        )}
        {!!identifiableValueSetups.length && (
          <SubTemplateCreationCheckbox
            id="copyIdentifiables"
            label={translate(
              'templateViewer.copyInheritedValueModal.identifiablesCheckbox',
            )}
            isChecked={didUserAcceptIdentifiableCreation}
            onToggle={() =>
              setDidUserAcceptIdentifiableCreation(
                !didUserAcceptIdentifiableCreation,
              )
            }
            paths={identifiableValueSetups.map(
              (requirement) => requirement.path,
            )}
          />
        )}
      </div>
    </SimpleModal>
  )
}
